import React, { useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Info } from 'lucide-react';

import { OrderSchemaType } from '../order.schema';
import { FormGrid, FormGroup, Separator, InfoLink } from '../style';
import InfoTooltip from '../../../../../components/InfoTooltip';

import CompanySelect from '../../../FashionOrders/SelectItem/CompanySelect';
import VendorSelect from '../../../FashionOrders/SelectItem/VendorSelect';



interface Company {
  id: number;
  companyName: string;
  cnpj?: string;
  cpf?: string;
  email?: string;
  phone?: string;
  contactName?: string;
  name?: string;
  contacts?: Array<{ name: string; email: string; phoneNumber: string }>;
  street?: string;
  number?: string | number;
  neighborhood?: string;
  city?: string;
  state?: string;
  cep?: string | number;
}

interface Seller {
  id: number;
  name: string;
  email?: string;
}

const MainInfosSection: React.FC = () => {
  const { control, watch, setValue } = useFormContext<OrderSchemaType>();

  const [activeInfo, setActiveInfo] = useState<string | null>(null);
  const [supplierObj, setSupplierObj] = useState<any>(null);
  const [sellerObj, setSellerObj] = useState<any>(null);
  const [clientObj, setClientObj] = useState<any>(null);

  const handleInfoToggle = (type: string, hasDetails: boolean) => {
    if (!hasDetails) return;
    setActiveInfo(activeInfo === type ? null : type);
  };

  const selectedSupplierId = watch('supplierId');
  const selectedClientId = watch('clientId');
  const selectedSellerId = watch('sellerId');

  const selectedSupplier = watch('supplierDetails');
  const selectedClient = watch('clientDetails');
  const selectedSeller = watch('sellerDetails');

  const selectedSupplierName = watch('supplier');
  const selectedClientName = watch('client');
  const selectedSellerName = watch('seller');



  const buildAddressString = (entity: any): string => {
    const safe = (v: any): string => {
      if (!v && v !== 0) return '';
      if (typeof v === 'string') return v;
      if (typeof v === 'number') return String(v);
      if (typeof v === 'object' && v !== null) return v.name || v.label || '';
      return '';
    };
    const street = safe(entity.street);
    const number = safe(entity.number);
    const neighborhood = safe(entity.neighborhood);
    const city = safe(entity.city);
    const state = safe(entity.state);
    const rawCep = entity.cep || '';
    const cepDigits = String(rawCep).replace(/\D/g, '');
    const cepPadded = cepDigits ? cepDigits.padStart(8, '0') : '';
    const cepFormatted = cepPadded ? `${cepPadded.slice(0, 5)}-${cepPadded.slice(5)}` : '';
    const parts: string[] = [];
    if (street) parts.push(`${street}${number ? `, ${number}` : ''}`);
    if (neighborhood) parts.push(neighborhood);
    if (city) parts.push(city);
    if (state) parts.push(state);
    const main = parts.join(' - ');
    const result = cepFormatted ? `${main}${main ? ' - ' : ''}${cepFormatted}` : main;
    return result.trim();
  };

  const getSupplierDetailsForTooltip = (supplier: any): Record<string, string> => {
    const details: Record<string, string> = {};
    if (supplier) {
      details['Razão Social'] = String(supplier.companyName || supplier.name || 'Não informado');
      if (supplier.isMain) {
        const tipo = supplier.tipoEstabelecimento || (supplier.cnpj ? (supplier.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '');
        if (tipo) details['Tipo'] = tipo;
      }
      details['CNPJ/CPF'] = String(supplier.cnpj || supplier.cpf || 'Não informado');
      details['Nome do Contato'] = String(supplier.contactName || supplier.contato || (supplier.contacts?.[0]?.name) || 'Não informado');
      details['Email'] = String(supplier.email || (supplier.contacts?.[0]?.email) || 'Não informado');
      details['Telefone'] = String(supplier.phone || supplier.telefone || (supplier.contacts?.[0]?.phoneNumber) || 'Não informado');
      const address = buildAddressString(supplier);
      if (address) details['Endereço'] = address;
    }
    return details;
  };

  const getClientDetailsForTooltip = (client: any): Record<string, string> => {
    const details: Record<string, string> = {};
    if (client) {
      details['Razão Social'] = String(client.companyName || client.name || 'Não informado');
      if (client.isMain) {
        const tipo = client.tipoEstabelecimento || (client.cnpj ? (client.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '');
        if (tipo) details['Tipo'] = tipo;
      }
      details['CNPJ/CPF'] = String(client.cnpj || client.cpf || 'Não informado');
      details['Nome do Contato'] = String(client.contactName || client.contato || (client.contacts?.[0]?.name) || 'Não informado');
      details['Email'] = String(client.email || (client.contacts?.[0]?.email) || 'Não informado');
      details['Telefone'] = String(client.phone || client.telefone || (client.contacts?.[0]?.phoneNumber) || 'Não informado');
      const address = buildAddressString(client);
      if (address) details['Endereço'] = address;
    }
    return details;
  };

  const getSellerDetailsForTooltip = (seller: any): Record<string, string> => {
    const details: Record<string, string> = {};
    if (seller) {
      details['Nome'] = String(seller.name || 'Não informado');
      details['Email'] = String(seller.email || 'Não informado');
    }
    return details;
  };

  const API_URL_COMPANIES = `${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`;
  const API_URL_SELLERS = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders/select-in-order/sellers`;

  return (
    <>
      <FormGrid>
        <FormGroup style={{ position: 'relative' }}>
          <Controller
            name="supplierId"
            control={control}
            render={({ field }) => (
              <CompanySelect
                label="Meu cnpj"
                apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`}
                displayField="companyName"
                queryParams={{ isMain: 'true' }}
                value={{ id: Number(field.value || 0), companyName: watch('supplier') || '' }}
                onChange={(v: any) => {
                  const supplierId = v.id || undefined;
                  field.onChange(supplierId);
                  setValue('supplier', v.companyName || '');
                  setValue('supplierId', supplierId);
                  setSupplierObj(v);
                  setActiveInfo(null);
                }}
              />
            )}
          />
          <InfoLink
            disabled={!selectedSupplierId}
            onClick={() => handleInfoToggle('supplier', !!selectedSupplierId)}
          >
            <Info size={12} />
            <span style={{ fontSize: '12px'}}>Mais informações</span>
          </InfoLink>
          {activeInfo === 'supplier' && selectedSupplier && (
            <InfoTooltip details={getSupplierDetailsForTooltip(selectedSupplier)} />
          )}
        </FormGroup>

        <FormGroup style={{ position: 'relative' }}>
          <Controller
            name="sellerId"
            control={control}
            render={({ field }) => (
              <VendorSelect<Seller>
                label="Vendedor"
                apiUrl={API_URL_SELLERS}
                displayField="name"
                value={(watch('sellerDetails') as Seller) || {
                  id: watch('sellerId') || 0,
                  name: watch('seller') || ''
                }}
                onChange={(newSeller) => {
                  field.onChange(newSeller.id || undefined);
                  setValue('seller', newSeller.name || '');
                  setValue('sellerId', newSeller.id || undefined, { shouldDirty: true });
                  setValue('sellerDetails', newSeller, { shouldDirty: true });
                  setActiveInfo(null);
                }}
              />
            )}
          />
          <InfoLink
            disabled={!selectedSellerId}
            onClick={() => handleInfoToggle('seller', !!selectedSellerId)}
          >
            <Info size={12} />
            <span style={{ fontSize: '12px'}}>Mais informações</span>
          </InfoLink>
          {activeInfo === 'seller' && selectedSeller && (
            <InfoTooltip details={getSellerDetailsForTooltip(selectedSeller)} />
          )}
        </FormGroup>

        <FormGroup style={{ position: 'relative' }}>
          <Controller
            name="clientId"
            control={control}
            render={({ field }) => (
              <CompanySelect<Company>
                label="Cliente"
                apiUrl={API_URL_COMPANIES}
                displayField="companyName"
                queryParams={{ companyType: 'Cliente' }}
                value={(watch('clientDetails') as Company) || {
                  id: watch('clientId') || 0,
                  companyName: watch('client') || ''
                }}
                onChange={(newClient) => {
                  field.onChange(newClient.id || undefined);
                  setValue('client', newClient.companyName || '');
                  setValue('clientId', newClient.id || undefined, { shouldDirty: true });
                  setValue('clientDetails', newClient, { shouldDirty: true });
                  setActiveInfo(null);
                }}
              />
            )}
          />
          <InfoLink
            disabled={!selectedClientId}
            onClick={() => handleInfoToggle('client', !!selectedClientId)}
          >
            <Info size={12} />
            <span style={{ fontSize: '12px'}}>Mais informações</span>
          </InfoLink>
          {activeInfo === 'client' && selectedClient && (
            <InfoTooltip details={getClientDetailsForTooltip(selectedClient)} />
          )}
        </FormGroup>
      </FormGrid>
      <Separator />
    </>
  );
};

export default MainInfosSection;